// React
import {
	ReactNode,
	Fragment,
	HTMLProps,
	useRef,
	useEffect,
	useState,
	Dispatch,
	SetStateAction,
} from "react"

// UI
import { cn } from "@/lib/utils"
import { ContentHint } from "@/components/ContentHint"
import { Tooltip } from "@/components/Tooltip"

// Types
import { SortingState, flexRender, type HeaderGroup } from "@/lib/table"

// Icons
import { FiChevronDown, FiChevronUp, FiInfo } from "@/lib/icons"

// CSS Styles
import "@/styles/ResponsiveTable.css"

// Types
interface TableProps extends HTMLProps<HTMLTableElement> {
	children: ReactNode
	layout?: "fixed" | "auto" | "responsive"
	isLoading?: boolean
	skeletonRows?: number
	headerGroups?: HeaderGroup<any>[]
}

/**
 * Main table component
 *
 * All tables are to be used trough TableItems instead of using HTML tables directly
 *
 * When `isLoading` is true, pass `headerGroups` from `table.getHeaderGroups()`
 * to render skeleton rows automatically instead of duplicating loading state markup.
 *
 * @param param0
 * @returns
 */
export const Table = ({
	children,
	className,
	layout = "fixed",
	isLoading = false,
	skeletonRows = 5,
	headerGroups,
	...rest
}: TableProps) => {
	const refTable = useRef<HTMLDivElement>(null)

	const tableClassName = cn(
		className,
		"w-full",
		layout === "fixed" && "2xl:table-fixed",
		layout === "responsive" && "responsive-table",
	)

	if (isLoading && headerGroups) {
		return (
			<ContentHint ref={refTable}>
				<div
					className="w-full overflow-auto print:overflow-visible"
					ref={refTable}
				>
					<table {...rest} className={tableClassName}>
						<TableHead>
							{headerGroups.map((headerGroup) => (
								<TableRowCell
									key={headerGroup.id}
									withHover={false}
								>
									{headerGroup.headers.map((header) => (
										<Fragment key={header.id}>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
										</Fragment>
									))}
								</TableRowCell>
							))}
						</TableHead>
						<TableBody>
							{Array.from({ length: skeletonRows }).map(
								(_, rowIndex) => (
									<TableRowCell
										key={rowIndex}
										isOdd={rowIndex % 2 === 0}
										withHover={false}
										data-testid="table-skeleton-row"
									>
										{headerGroups[0]?.headers.map(
											(header) => (
												<TableDataCell key={header.id}>
													<span
														data-testid="table-skeleton-cell"
														className="inline-block h-4 w-3/4 animate-pulse rounded bg-gray-200"
													/>
												</TableDataCell>
											),
										)}
									</TableRowCell>
								),
							)}
						</TableBody>
					</table>
				</div>
			</ContentHint>
		)
	}

	return (
		<ContentHint ref={refTable}>
			<div
				className="w-full overflow-auto print:overflow-visible"
				ref={refTable}
			>
				<table {...rest} className={tableClassName}>
					{children ?? null}
				</table>
			</div>
		</ContentHint>
	)
}

export const TableBody = ({
	className,
	children,
	...rest
}: HTMLProps<HTMLTableSectionElement>) => {
	return (
		<tbody className={cn(className, "border-b border-gray-200")} {...rest}>
			{children ?? null}
		</tbody>
	)
}

export const TableHead = ({
	children,
	...rest
}: Omit<HTMLProps<HTMLTableSectionElement>, "className">) => {
	return (
		<thead
			className="sticky top-0 border-y border-gray-200 border-t-gray-100 bg-gray-50"
			{...rest}
		>
			{children}
		</thead>
	)
}

type TableDataCellProps = HTMLProps<HTMLTableDataCellElement> & {
	multiline?: boolean
	showHeaderOnlyOnMobile?: boolean
	fieldName?: string
}

/**
 * TableDataCell
 */
export const TableDataCell = ({
	className = "",
	children,
	multiline = false,
	showHeaderOnlyOnMobile = false,
	fieldName,
	...props
}: TableDataCellProps) => {
	return (
		<>
			{/** Responsive identifier header */}
			{showHeaderOnlyOnMobile === true && fieldName && (
				<p
					className={cn(
						"sm:hidden",
						"text-left font-medium tracking-wider text-gray-500",
						"border-t border-b border-gray-200 border-t-gray-100 bg-white",
						"px-[0.5rem] pt-6 pb-2",
					)}
				>
					{children}
				</p>
			)}

			{/** Default table cell */}

			<td
				className={cn(
					showHeaderOnlyOnMobile === true && "!hidden sm:!block",
					"px-6 py-4 text-sm text-gray-700",
					// oxlint-disable-next-line tailwindcss/no-conflicting-classes
					"truncate whitespace-nowrap", // Default is single-line
					multiline && "break-word whitespace-pre-wrap", // Multiline optional

					// Classname overrides
					className,
				)}
				{...props}
			>
				{/* Header label */}
				{fieldName && (
					<p className="mr-auto flex items-center text-xs tracking-wider text-gray-500 uppercase sm:hidden">
						{fieldName}
					</p>
				)}

				{/* Cell content */}
				{children ?? null}
			</td>
		</>
	)
}

/**
 * TableRowCell
 * @param param0
 * @returns
 */
interface TableRowCellProps extends HTMLProps<HTMLTableRowElement> {
	isOdd?: boolean
	variant?: TableRowCellVariant
	withHover?: boolean
	children: ReactNode
}
export type TableRowCellVariant = "neutral" | "valid" | "invalid"
export const TableRowCell = ({
	isOdd,
	variant = "neutral",
	withHover = true,
	className = "",
	children,
	...rest
}: TableRowCellProps) => {
	const applyEvenOrOddstyles = {
		neutral: isOdd ? "bg-gray-50" : "bg-white",
		invalid: isOdd ? "bg-red-200" : "bg-red-100",
		valid: isOdd ? "bg-green-200" : "bg-green-100",
	}

	return (
		<tr
			className={cn(
				applyEvenOrOddstyles[variant],
				withHover && "hover:bg-gray-100",
				className,
			)}
			{...rest}
		>
			{children ?? null}
		</tr>
	)
}

interface TableHeadingProps extends HTMLProps<HTMLTableHeaderCellElement> {
	// "sortable" includes hover styles
	variant?: "static" | "sortable"
	as?: "td" | "th"
	fieldName?: string
	sortType?: "client" | "server"
	sort?: string | SortingState
	setSort?:
		| ((sort: string) => void)
		| ((value: SetStateAction<SortingState>) => void)
	tooltip?: string
}

/**
 * TableHeading
 */
export const TableHeading = ({
	children,
	variant = "sortable",
	fieldName,
	sort,
	sortType = "server",
	setSort,
	className = "",
	tooltip,
	...rest
}: TableHeadingProps) => {
	// State
	const [isSorted, setIsSorted] = useState(false)
	const [isSortedDesc, setIsSortedDesc] = useState(false)

	// When sort is changed
	useEffect(() => {
		if (sort && fieldName) {
			// Server sorting uses django string prefixed with "-"
			if (sortType === "server" && typeof sort === "string") {
				setIsSorted(sort.endsWith(fieldName))
				setIsSortedDesc(
					sort.endsWith(fieldName) && sort.startsWith("-"),
				)
			}

			// Client sorting uses an array of objects with id and desc
			if (sortType === "client" && Array.isArray(sort)) {
				setIsSorted(sort[0]?.id === fieldName)
				setIsSortedDesc(!!sort[0]?.desc)
			}
		}
	}, [sort, fieldName, sortType])

	// Handle Click
	const handleClick = () => {
		// Sort header if sorting is enabled
		if (fieldName && setSort) {
			// Server sorting uses django string prefixed with "-"
			if (sortType === "server") {
				const serverSetSort = setSort as (sort: string) => void
				if (!isSorted && !isSortedDesc) {
					serverSetSort(`-${fieldName}`)
				} else if (isSortedDesc) {
					serverSetSort(fieldName)
				} else {
					serverSetSort("")
				}
			}

			// Client sorting uses an array of objects with id and desc
			if (sortType === "client") {
				const clientSetSort = setSort as Dispatch<
					SetStateAction<SortingState>
				>
				clientSetSort([
					{
						id: fieldName,
						desc: isSorted ? !isSortedDesc : true,
					},
				])
			}
		}
	}

	// Template
	return (
		<th
			{...rest}
			scope="col"
			aria-sort={
				isSorted
					? isSortedDesc
						? "descending"
						: "ascending"
					: undefined
			}
			className={cn(
				"px-6 py-3 text-left",
				className,
				variant === "sortable" && "cursor-pointer hover:bg-gray-200",
			)}
			onClick={() => handleClick()}
		>
			<span className="flex items-center text-xs font-medium tracking-wider text-gray-500 uppercase">
				{children ?? null}
				{tooltip && (
					<Tooltip content={tooltip}>
						<span className="-m-1.5 ml-1 inline-flex size-8 items-center justify-center">
							<FiInfo
								size={16}
								className="text-gray-400 hover:text-gray-600"
								aria-label="More information"
							/>
						</span>
					</Tooltip>
				)}
				{isSorted && (
					<span className="ml-2" aria-hidden="true">
						{isSortedDesc ? (
							<FiChevronDown size={16} />
						) : (
							<FiChevronUp size={16} />
						)}
					</span>
				)}
			</span>
		</th>
	)
}

export const TableFooter = ({
	className,
	...rest
}: HTMLProps<HTMLTableSectionElement>) => {
	return (
		<tfoot
			className={cn(
				className,
				"border-b border-gray-200 bg-gray-50 text-sm",
			)}
			{...rest}
		/>
	)
}
